import FeedbackSettingsDialog from 'components/CreateAssignment/FeedbackSettingsDialog';
import InlineCommentsSettingsDialog from 'components/CreateAssignment/InlineCommentsSettingsDialog';
import StudentFeedbackSettingsDialog from 'components/CreateAssignment/StudentFeedbackSettingsDialog';
import { ADMIN_OVERRIDE_STATE } from 'components/Settings';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import useAutoSaveForm from 'hooks/settings/useAutoSaveForm';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import userService from 'services/user';
import { assignmentDefaultsFromApi, assignmentDefaultsToApi } from 'utils/assignmentDefaults';

export const PreferenceContext = createContext(null);

export const usePreferenceSettings = () => useContext(PreferenceContext);

const FEEDBACK_SETTING_KEYS = Object.freeze({
    feedback: { settingsKey: 'feedbackSettings', contextKey: 'feedbackCustomContext' },
    formative: { settingsKey: 'studentFeedbackSettings', contextKey: 'studentFeedbackCustomContext' },
});

const getFeedbackSettingsValues = ({
    structure,
    numSuggestions,
    suggestionLength,
    languageLevel,
    tone,
    customInstructions,
}) => ({
    structure,
    numSuggestions,
    suggestionLength,
    languageLevel,
    tone,
    customInstructions,
});

export const PreferenceProvider = ({ children, onDirtyChange }) => {
    const { isGradingFeatureDisabled, isFeedbackFeatureDisabled, isInlineCommentsFeatureDisabled } = useApp();
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const userId = auth?.user_id;
    const initialDefaults = useMemo(() => assignmentDefaultsFromApi(null), []);
    const [feedbackDialog, setFeedbackDialog] = useState(null);
    const [inlineDialog, setInlineDialog] = useState(false);
    const loadedUserIdRef = useRef(null);
    const preferences = useAutoSaveForm({
        enabled: Boolean(userId),
        initialValues: initialDefaults,
        getErrorMessage: () => '',
        persist: (next) => userService.updateAssignmentDefaults(userId, assignmentDefaultsToApi(next)),
        onSuccess: () => {
            message.success(t('common:message.preferences-updated', 'Preferences have been updated.'));
            onDirtyChange?.(false);
        },
        onError: () => message.error(t('message.something-went-wrong', 'Something went wrong.')),
    });
    const { form: defaults, reset, updateAndSave, replaceAndSave, save, status } = preferences;
    const saving = status === 'saving';

    useEffect(() => {
        if (!userId || loadedUserIdRef.current === userId) return;

        const loadPreferences = async () => {
            loadedUserIdRef.current = userId;
            setFeedbackDialog(null);
            setInlineDialog(false);
            try {
                const preferences = await userService.getAssignmentDefaults(userId);
                if (loadedUserIdRef.current !== userId) return;
                reset(assignmentDefaultsFromApi(preferences));
            } catch (error) {
                // The original product defaults remain the safe fallback.
                if (loadedUserIdRef.current !== userId) return;
                reset(assignmentDefaultsFromApi(null));
            }
        };

        loadPreferences();
    }, [reset, userId]);

    const adminOverrideStates = useMemo(
        () => ({
            grading: isGradingFeatureDisabled ? ADMIN_OVERRIDE_STATE.OVERRIDDEN : ADMIN_OVERRIDE_STATE.MAY_OVERRIDE,
            feedback: isFeedbackFeatureDisabled ? ADMIN_OVERRIDE_STATE.OVERRIDDEN : ADMIN_OVERRIDE_STATE.MAY_OVERRIDE,
            'formative-feedback': isFeedbackFeatureDisabled
                ? ADMIN_OVERRIDE_STATE.OVERRIDDEN
                : ADMIN_OVERRIDE_STATE.MAY_OVERRIDE,
            'inline-comments': isInlineCommentsFeatureDisabled
                ? ADMIN_OVERRIDE_STATE.OVERRIDDEN
                : ADMIN_OVERRIDE_STATE.MAY_OVERRIDE,
        }),
        [isFeedbackFeatureDisabled, isGradingFeatureDisabled, isInlineCommentsFeatureDisabled]
    );

    const update = useCallback(
        (key, value) => {
            onDirtyChange?.(true);
            return updateAndSave(key, value);
        },
        [onDirtyChange, updateAndSave]
    );

    const saveFeedbackSettings = useCallback(
        (next, type) => {
            const { settingsKey, contextKey } = FEEDBACK_SETTING_KEYS[type];
            const nextDefaults = {
                ...defaults,
                [settingsKey]: {
                    ...defaults[settingsKey],
                    ...getFeedbackSettingsValues(next),
                },
                [contextKey]: next[contextKey] || '',
            };
            onDirtyChange?.(true);
            replaceAndSave(nextDefaults);
            setFeedbackDialog(null);
        },
        [defaults, onDirtyChange, replaceAndSave]
    );

    const value = useMemo(
        () => ({
            defaults,
            saving,
            update,
            save,
            feedbackDialog,
            setFeedbackDialog,
            inlineDialog,
            setInlineDialog,
            isGradingFeatureDisabled,
            isFeedbackFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            adminOverrideStates,
        }),
        [
            defaults,
            feedbackDialog,
            inlineDialog,
            isFeedbackFeatureDisabled,
            isGradingFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            adminOverrideStates,
            save,
            saving,
            update,
        ]
    );

    const feedbackSettings =
        feedbackDialog === 'formative' ? defaults.studentFeedbackSettings : defaults.feedbackSettings;
    const feedbackForm = {
        ...defaults,
        rubric: null,
        feedbackSettings,
        feedbackCustomContext:
            feedbackDialog === 'formative' ? defaults.studentFeedbackCustomContext : defaults.feedbackCustomContext,
    };

    return (
        <PreferenceContext.Provider value={value}>
            {children}
            <FeedbackSettingsDialog
                isTemplatedFeedbackAvailable={false}
                open={feedbackDialog === 'feedback'}
                form={feedbackForm}
                onClose={() => setFeedbackDialog(null)}
                onSave={(next) => saveFeedbackSettings(next, 'feedback')}
            />
            <StudentFeedbackSettingsDialog
                isTemplatedFeedbackAvailable={false}
                open={feedbackDialog === 'formative'}
                form={feedbackForm}
                onClose={() => setFeedbackDialog(null)}
                showAssignmentSettingsCopy={false}
                onSave={(next) => saveFeedbackSettings(next, 'formative')}
            />
            <InlineCommentsSettingsDialog
                open={inlineDialog}
                form={defaults}
                onClose={() => setInlineDialog(false)}
                onSave={(next) => {
                    const nextDefaults = {
                        ...defaults,
                        inlineCommentsSettings: {
                            ...defaults.inlineCommentsSettings,
                            maxCommentsPerPage: next.maxCommentsPerPage,
                            commentLength: next.commentLength,
                            languageLevel: next.languageLevel,
                            tone: next.tone,
                        },
                        inlineCommentsCustomContext: next.inlineCommentsCustomContext || '',
                    };
                    onDirtyChange?.(true);
                    replaceAndSave(nextDefaults);
                    setInlineDialog(false);
                }}
            />
        </PreferenceContext.Provider>
    );
};
